import { Injectable } from "@angular/core";
import { ApiService } from "@services/api.service";
import {
	IResponseAPIListado,
	IResponseAPIObjeto,
} from "@models/responseAPI.interface";
import { DangerError } from "@shared/errors/custom.error";
import { assignToModel } from "@shared/utils/fieldsMap.utils";
import { Trabajador } from "@models/trabajador.model";
import { SessionService } from "./session.service";
import { Archivo } from "@models/archivo.model";
import { Licencia } from "@models/licencia.model";
import { Persona } from "@models/persona.model";
import { DocumentoService } from "./documento.service";
import { StateService } from "./state.service";
import { environment } from "@env/environment";

@Injectable({
	providedIn: "root",
})
export class LicenciaService {
	rutSinDV: number;
	isLicencia: 1 = 1;
  private env = environment.env;

	constructor(
		private sessionService: SessionService,
		private apiService: ApiService,
		private documentoService: DocumentoService,
		private stateService: StateService,
	) {
		this.rutSinDV = this.sessionService.getSesionActual().getRutSinDV();
	}

	async obtenerLicenciasPorPrestador(
		rut: number,
		idRegion: number,
	): Promise<Licencia[]> {
		const { data, msg, success } =
			await this.apiService.privatePost<IResponseAPIListado>(
				`listado-licencias`,
				{
					rut,
					idRegion,
				},
			);
		if (!success || data.length === 0) throw new DangerError(msg);
		return data.map((l: any) => {
			const [dia, mes, año] = l.fecha_emision.split("-");
			if (l.run_trabajador) {
				const [rut, dv] = l.run_trabajador.split("-");
				l.rut = rut;
				l.dv = dv;
			}
			l.trabajador = assignToModel(Trabajador, l, {
				nombres: "nombre_trabajador",
			});
			l.fecha_emision = `${año}-${mes}-${dia}`;
			l.compin = {
				codigo: l.cod_compin,
				nombre: l.nom_compin,
			};
			l.estado = {
				nombre: l.nom_estado,
			};
			return assignToModel(Licencia, l, { id: "id_licencia", num_licencia: "num_licencia" });
		});
	}

	async obtenerLicenciasPorExpediente(id: number): Promise<Licencia[]> {
		const { data, msg, success } =
			await this.apiService.privatePost<IResponseAPIListado>(
				`listar-licencias-expediente`,
				{ exp_id: id },
			);

		if (!success && data.length === 0) throw new DangerError(msg);
		return data.map((l: any) => {
			l.trabajador = assignToModel(Trabajador, l, {
				rut: "lic_rut_trabajador",
				dv: "lic_digver_trabajador",
				nombres: "lic_nombre_trabajador",
			});
			l.empleador = assignToModel(Persona, l, {
				rut: "lic_rut_empleador",
				dv: "lic_digver_empleador",
				nombres: "lic_nombre_empleador",
			});
			l.porcentajesDocumentos = {
				obligatoriosIngresados: l.doctos_oblig_ingresados,
				obligatoriosValidados: l.doctos_oblig_validados,
				opcionalesValidados: l.doctos_opcio_validados,
			};
			l.estado = {
				codigo: l.eli_codigo,
				nombre: l.eli_nombre,
			};
			((l.estadoContraloria = {
				codigo: l.ere_codigo,
				nombre: l.ere_nombre,
			}),
				(l.compin = {
					codigo: l.cmp_codigo,
					nombre: l.cmp_nombre,
				}));
			return assignToModel(Licencia, l, {
				id: "lic_id",
				numero: "lic_num_lm",
				dias_otorgados: "lic_dias_otorgados",
				fecha_creacion: "lic_fecha_creacion",
				fecha_emision: "lic_fecha_emision",
				fecha_recepcion: "lic_fecha_recepcion",
			});
		});
	}

	async ingresarDocumentosLicencia(
		idExpediente: number,
		idLicencia: number,
		file: Archivo,
		existe: boolean,
	): Promise<string> {
		const body: any = {
			idExpediente,
			idLicencia,
			base64: file.base64,
			nombre: file.nombre,
			tipoDocumento: file.tipo,
			run: this.rutSinDV,
			existe,
		};
		if (existe) body.noValido = 2;
		const { data, msg, success } =
			await this.apiService.privatePost<IResponseAPIListado>(
				`licencia-subir-documento`,
				body,
			);
		const errorMsg: string = (data[0] as unknown as string) ?? msg;
		if (!success) throw new DangerError(errorMsg);
		return msg;
	}

	async obtenerDocumentosLicencia(
		id: number,
		mostrarError: boolean = true,
	): Promise<Archivo[]> {
		try {
			const { data, msg, success } = await this.documentoService.obtenerDocumentos(this.isLicencia, id);
			if (!mostrarError && (!success || data.length === 0)) throw new DangerError(msg);
			return data;
		} catch (error) {
			if (!mostrarError) return [];
			throw error;
		}
	}

	async guardarValidacionDocumento(
		documentId: number,
		documentoValidacion: string,
		motivo: string,
	): Promise<string> {
		const body = {
			documentId,
			documentoValidacion,
			rutUsuario: this.rutSinDV,
			motivo,
		};
		const { msg, data, success } =
			await this.apiService.privatePost<IResponseAPIObjeto>(
				`guardar-validacion-documento`,
				body,
			);
		if (!success) throw new DangerError(msg);
		const expediente = this.stateService.getExpediente();
		expediente.exp_cierra_expediente = data?.exp_cierra_expediente?.toString() ?? "NO";
		this.stateService.setExpediente(expediente);

		return msg;
	}
}
